import React, { useState, useMemo, useEffect } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { ArrowLeft, FolderUp, AlertTriangle, Clock } from "lucide-react";
import { LoadingOverlay } from "../layouts/LoadingOverlay";
import { Toaster, toast } from "sonner";
import { EnvioDocumentosModal } from "../shared/EnvioDocumentosModal";

import { PersonalDataForm } from "../forms/PersonalDataForm";
import { AddressForm } from "../forms/AddressForm";
import { BankDataForm } from "../forms/BankDataForm";
import { CustomProcessList } from "../process/CustomProcessList";
import { CondicoesCessaoSection } from "../forms/CondicoesCessaoSection";
import { isPixKeyValid } from "./userFormValidation";
import { personalDataSchema, addressSchema, bankDataSchema } from "../../schemas/userSchemas";
import { isValidEmail, isValidPhoneBR, isValidCPF } from "../../lib/utils";
import { useThemeStore } from "../../store/useThemeStore";
import { DEFAULT_FORM_SECTIONS } from "../../lib/theme";

export function CustomFlowView({
  userData,
  bancoData,
  selectedProcessIds,
  setSelectedProcessIds,
  isSubmitting,
  handleUserDataChange,
  handleBancoChange,
  handleSubmit,
  isAdminMode,
  onReturnToAdmin,
  onSubmitSuccess,
  initializeProcesses,
}) {
  const [errors, setErrors] = useState({});
  const [isFalecido, setIsFalecido] = useState(false);
  const [envioModalProcessoId, setEnvioModalProcessoId] = useState(null);
  const [envioModalProcessoNome, setEnvioModalProcessoNome] = useState(null);
  const { theme } = useThemeStore();
  const sectionsOrder = theme?.form_sections_order || DEFAULT_FORM_SECTIONS;

  useEffect(() => {
    if (userData) {
      initializeProcesses();
    }
  }, [userData, initializeProcesses]);

  useEffect(() => {
    if (window.location.search) {
      window.history.replaceState(null, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredProcesses = useMemo(() => {
    if (!userData?.processos) return [];
    return userData.processos.filter(p => p.tipo_aceite !== 'cessao' && p.processo?.ativo !== false);
  }, [userData]);

  const inactiveProcesses = useMemo(
    () => (userData?.processos ?? []).filter(p => p.processo?.ativo === false),
    [userData],
  );

  const processesComValores = useMemo(() =>
    filteredProcesses.filter(p => p.processo != null && p.processo.tem_valores !== false),
  [filteredProcesses]);

  const bankUpdateLocked = useMemo(() => {
    if (!filteredProcesses.length) return false;
    const hasBankData = !!(userData?.banco || userData?.agencia || userData?.conta_corrente);
    if (!hasBankData) return false;
    return filteredProcesses.some(p => p.processo?.permite_atualizacao_bancaria !== true);
  }, [filteredProcesses, userData]);

  const cessaoProcesses = useMemo(() => {
    if (!userData?.processos) return [];
    return userData.processos.filter(p => p.tipo_aceite === 'cessao' && p.processo?.ativo !== false);
  }, [userData]);

  const hasPendingAgreement = useMemo(() => {
    return filteredProcesses.some(p => !p.tipo_aceite && !p.requer_recalculo);
  }, [filteredProcesses]);

  const lockedPending = useMemo(() => {
    return filteredProcesses.filter(p => !p.tipo_aceite && p.requer_recalculo);
  }, [filteredProcesses]);

  const allLockedInAnalise = useMemo(() => {
    return lockedPending.length > 0 && lockedPending.every(p => p.status === "analise");
  }, [lockedPending]);

  const renderDocButtons = (list) => (
    <div className="flex flex-col gap-2 w-full mt-2">
      {[...new Map(list.map(p => [p.processo_id, p])).values()].map(pc => (
        pc.status === "analise" ? (
          <div
            key={pc.processo_id}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-statusInfoBg border border-statusInfo/20 text-statusInfo text-sm font-semibold w-full"
          >
            <Clock className="w-4 h-4 shrink-0" />
            {pc.processo?.nome_processo
              ? `Documentos em análise — ${pc.processo.nome_processo}`
              : "Documentos em análise"}
          </div>
        ) : (
          <button
            key={pc.processo_id}
            type="button"
            onClick={() => {
              setEnvioModalProcessoId(pc.processo_id);
              setEnvioModalProcessoNome(pc.processo?.nome_processo || null);
            }}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-bold active:scale-[0.98] transition-all shadow-sm w-full"
            style={{ touchAction: "manipulation" }}
          >
            <FolderUp className="w-4 h-4 shrink-0" />
            {pc.processo?.nome_processo
              ? `Enviar documentos — ${pc.processo.nome_processo}`
              : "Enviar documentos"}
          </button>
        )
      ))}
    </div>
  );

  const pixKeyInvalid = !isPixKeyValid(bancoData, isValidCPF, isValidEmail, isValidPhoneBR);

  const handleFormSubmit = async () => {
    const personalResult = personalDataSchema.safeParse(userData);
    const addressResult = addressSchema.safeParse(userData);
    const bankResult = bankDataSchema.safeParse(bancoData);

    const newErrors = {};
    if (!personalResult.success) {
      personalResult.error.issues.forEach(issue => newErrors[issue.path[0]] = issue.message);
    }
    if (!addressResult.success) {
      addressResult.error.issues.forEach(issue => newErrors[issue.path[0]] = issue.message);
    }
    if (!bankResult.success) {
      bankResult.error.issues.forEach(issue => newErrors[issue.path[0]] = issue.message);
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Por favor, preencha todos os campos obrigatórios destacados.");
      return;
    }

    setErrors({});

    const success = await handleSubmit("acordo", isFalecido);
    if (success) {
      onSubmitSuccess();
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-4 bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-background dark:via-card dark:to-muted w-full overflow-y-auto">
      <Toaster richColors />

      {isSubmitting && (
        <LoadingOverlay message="Estamos gerando seus contratos, isso pode demorar alguns segundos." />
      )}

      <div className="max-w-5xl mx-auto pb-8">
        {isAdminMode && (
          <Button
            variant="ghost"
            onClick={onReturnToAdmin}
            className="mb-6 gap-2 hover:bg-card/80 transition-all shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao Painel de Admin
          </Button>
        )}

        <Card className="p-4 sm:p-8 md:p-10 shadow-sm border border-border bg-card/95 backdrop-blur-sm overflow-visible">
          {userData ? (
            <>
              {sectionsOrder.map((section, idx) => {
                const isLast = idx === sectionsOrder.length - 1;
                const sep = !isLast && (
                  <Separator key={`sep-${section}`} className="my-6 md:my-10 bg-gradient-to-r from-transparent via-border to-transparent h-px" />
                );

                if (section === "dados_pessoais") return (
                  <React.Fragment key={section}>
                    <PersonalDataForm
                      userData={userData}
                      handleUserDataChange={handleUserDataChange}
                      errors={errors}
                      isAdminMode={isAdminMode}
                      setIsFalecido={setIsFalecido}
                      section="top"
                    />
                    {sep}
                  </React.Fragment>
                );

                if (section === "dados_pessoais_extra") return (
                  <React.Fragment key={section}>
                    <PersonalDataForm
                      userData={userData}
                      handleUserDataChange={handleUserDataChange}
                      errors={errors}
                      isAdminMode={isAdminMode}
                      setIsFalecido={setIsFalecido}
                      section="bottom"
                    />
                    {sep}
                  </React.Fragment>
                );

                if (section === "valores") {
                  if (processesComValores.length === 0 && cessaoProcesses.length === 0 && inactiveProcesses.length === 0) return null;
                  return (
                    <React.Fragment key={section}>
                      {inactiveProcesses.length > 0 && (
                        <div role="status" className="p-4 rounded-2xl bg-statusWarningBg border border-statusWarning/20 text-sm text-statusWarning flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                          <p>
                            Processo temporariamente indisponível para aceite:{" "}
                            <strong>{inactiveProcesses.map(p => p.processo?.nome_processo).filter(Boolean).join(", ")}</strong>.
                            Tente novamente mais tarde.
                          </p>
                        </div>
                      )}
                      {processesComValores.length > 0 && (
                        <CustomProcessList
                          processes={processesComValores}
                          selectedIds={selectedProcessIds}
                          setSelectedIds={setSelectedProcessIds}
                          hideCheckbox={true}
                        />
                      )}
                      {cessaoProcesses.length > 0 && (
                        <CustomProcessList
                          processes={cessaoProcesses}
                          selectedIds={[]}
                          setSelectedIds={() => {}}
                          hideCheckbox={true}
                        />
                      )}
                      {sep}
                    </React.Fragment>
                  );
                }

                if (section === "endereco") return (
                  <React.Fragment key={section}>
                    <AddressForm
                      userData={userData}
                      handleUserDataChange={handleUserDataChange}
                      errors={errors}
                    />
                    {sep}
                  </React.Fragment>
                );

                if (section === "dados_bancarios") return (
                  <React.Fragment key={section}>
                    <BankDataForm
                      bancoData={bancoData}
                      handleBancoChange={handleBancoChange}
                      errors={errors}
                      pixKeyInvalid={pixKeyInvalid}
                      readOnly={bankUpdateLocked}
                    />
                    {sep}
                  </React.Fragment>
                );

                if (section === "condicoes_cessao") {
                  const allProcesses = userData?.processos ?? [];
                  const comCondicoes = allProcesses.filter(p => {
                    const c = p.processo?.condicoes_cessao;
                    return p.processo?.tem_cessao !== false && c &&
                      ((Array.isArray(c?.pontos) && c.pontos.length > 0) ||
                       (Array.isArray(c?.clausulas) && c.clausulas.length > 0));
                  });
                  if (comCondicoes.length === 0) return null;
                  return (
                    <React.Fragment key={section}>
                      <CondicoesCessaoSection processos={allProcesses} userData={userData} />
                      {sep}
                    </React.Fragment>
                  );
                }

                if (section === "envio_documentos") {
                  if (!userData?.processos?.length) return null;
                  const processos = [...new Map(userData.processos.map(p => [p.processo_id, p])).values()]
                    .filter(pc =>
                      pc.requer_recalculo ||
                      pc.processo?.permitir_envio_documentos === true ||
                      userData?.pendente_documentacao
                    );
                  if (processos.length === 0) return null;
                  return (
                    <React.Fragment key={section}>
                      <div className="p-4 rounded-2xl bg-statusInfoBg border border-statusInfo/20">
                        <p className="text-sm font-semibold text-statusInfo mb-3 flex items-center gap-2">
                          <FolderUp className="w-4 h-4 shrink-0" />
                          Envio de documentos
                        </p>
                        <div className="flex flex-col gap-2">
                          {processos.map(pc => {
                            if (pc.status === "analise") {
                              return (
                                <div
                                  key={pc.processo_id}
                                  className="flex items-center gap-2 px-4 py-3 rounded-xl bg-statusInfoBg border border-statusInfo/20 text-statusInfo text-sm font-medium w-full text-left"
                                >
                                  <Clock className="w-4 h-4 shrink-0 text-blue-500" />
                                  <span className="flex-1">
                                    {pc.processo?.nome_processo
                                      ? `Documentos em análise — ${pc.processo.nome_processo}`
                                      : "Documentos em análise"}
                                  </span>
                                </div>
                              );
                            }
                            const obrigatorio = !!pc.requer_recalculo;
                            return (
                              <button
                                key={pc.processo_id}
                                type="button"
                                onClick={() => {
                                  setEnvioModalProcessoId(pc.processo_id);
                                  setEnvioModalProcessoNome(pc.processo?.nome_processo || null);
                                }}
                                className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium active:scale-[0.98] transition-all shadow-sm w-full text-left ${
                                  obrigatorio
                                    ? "bg-statusWarningBg border-statusWarning/30 text-statusWarning hover:bg-statusWarningBg hover:border-statusWarning/40"
                                    : "bg-card border-statusInfo/20 text-statusInfo hover:bg-statusInfoBg hover:border-statusInfo/30"
                                }`}
                                style={{ touchAction: "manipulation" }}
                              >
                                {obrigatorio
                                  ? <AlertTriangle className="w-4 h-4 shrink-0 text-statusWarning" />
                                  : <FolderUp className="w-4 h-4 shrink-0 text-blue-500" />}
                                <span className="flex-1">
                                  {pc.processo?.nome_processo
                                    ? `Enviar documentos — ${pc.processo.nome_processo}`
                                    : "Enviar documentos"}
                                </span>
                                {obrigatorio && (
                                  <span className="text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-200 text-statusWarning shrink-0">
                                    Obrigatório
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      {sep}
                    </React.Fragment>
                  );
                }

                return null;
              })}

              {hasPendingAgreement ? (
                <>
                  {lockedPending.length > 0 && (
                    <div className={`mt-10 p-4 rounded-2xl border ${allLockedInAnalise ? "bg-statusInfoBg border-statusInfo/20 text-statusInfo" : "bg-statusWarningBg border-statusWarning/20 text-statusWarning"}`}>
                      <div className="flex items-start gap-2 text-sm font-medium">
                        {allLockedInAnalise
                          ? <Clock className="w-5 h-5 shrink-0 text-blue-500 mt-0.5" />
                          : <AlertTriangle className="w-5 h-5 shrink-0 text-statusWarning mt-0.5" />}
                        <span>{allLockedInAnalise
                          ? "Seus documentos estão em análise. Aguarde o retorno da equipe para prosseguir com essas propostas."
                          : "Uma ou mais propostas estão em revisão e precisam de recálculo. Envie os documentos abaixo para que possamos prosseguir com elas."}</span>
                      </div>
                      {renderDocButtons(lockedPending)}
                    </div>
                  )}
                  <div className={`${lockedPending.length > 0 ? "mt-6" : "mt-10"} p-4 rounded-2xl bg-muted border border-border flex justify-center`}>
                    <Button
                      onClick={handleFormSubmit}
                      disabled={isSubmitting}
                      className="w-full px-4 py-5 text-sm sm:text-base font-black active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed leading-snug"
                    >
                      {isSubmitting ? "Enviando documentos..." : (theme.aceite_button_text || "Aceitar proposta e enviar para assinatura")}
                    </Button>
                  </div>
                </>
              ) : lockedPending.length > 0 ? (
                <div className={`mt-10 p-6 rounded-2xl border text-center flex flex-col items-center gap-2 ${allLockedInAnalise ? "bg-statusInfoBg border-statusInfo/20 text-statusInfo" : "bg-statusWarningBg border-statusWarning/20 text-statusWarning"}`}>
                  {allLockedInAnalise
                    ? <Clock className="w-7 h-7 text-blue-500" />
                    : <AlertTriangle className="w-7 h-7 text-statusWarning" />}
                  <p className="text-sm font-semibold">{allLockedInAnalise
                    ? "Seus documentos estão em análise. Aguarde o retorno da equipe."
                    : "Sua proposta está em revisão. Envie os documentos abaixo para prosseguir."}</p>
                  {renderDocButtons(lockedPending)}
                </div>
              ) : (
                <div className="mt-10 p-6 rounded-2xl bg-statusSuccessBg border border-statusSuccess/20 text-statusSuccess text-center text-lg font-black">
                  {theme.aceite_success_text || "Você já aceitou o acordo, verifique seu Whatsapp"}
                </div>
              )}

            </>
          ) : (
            <div className="text-center text-muted-foreground py-12">
              <p className="text-lg">
                Selecione um usuário no painel de admin para ver os detalhes.
              </p>
            </div>
          )}
        </Card>
      </div>

      {envioModalProcessoId && (
        <EnvioDocumentosModal
          processoId={envioModalProcessoId}
          processoNome={envioModalProcessoNome}
          userData={userData}
          onClose={() => { setEnvioModalProcessoId(null); setEnvioModalProcessoNome(null); }}
        />
      )}
    </div>
  );
}
